/**
 * NALLI — Universal dataLayer (Phase B, 2026-08-10).
 *
 * Auto-instruments every section and banner with zero per-section config:
 * identity is derived from Shopify's own DOM conventions
 * (`#shopify-section-…` roots, slideshow-slide order, link/image content),
 * so a merchant adding a new banner in the editor starts emitting
 * view/click events automatically.
 *
 * Transport: every event goes BOTH ways.
 *   1. `window.dataLayer.push({event, …})`   (GTM-shape, future container-ready)
 *   2. gtag arguments-push with `send_to: GA4_ID` through the Google tag the
 *      native Google & YouTube app already loads (no GTM container needed;
 *      decision 2026-08-10: use what is already connected, zero extra JS).
 *      VERIFIED live on nalli.com 2026-08-23: the app loads gtag/js on the MAIN
 *      window (NOT inside the web-pixel sandbox) and configures G-L2V9BMCRBL,
 *      AW-917352930 and MC-04LZT531W5, so `send_to` has a real destination.
 *      Path 2 is gated on that config actually landing (see transport block).
 *
 * Event map:
 *   view_promotion / select_promotion  — GA4 STANDARD promo events (the native
 *     app never sends these, so no collision). promotion_id/name/creative_slot
 *     identify the exact banner/slide.
 *   nalli_section_view — first 50%-visibility of each section per pageview.
 *   nalli_menu_click, nalli_wishlist_toggle, nalli_quick_view,
 *   nalli_carousel_nav, nalli_search_open, nalli_drawer_open — interactions
 *     the standard stack misses.
 *
 * Perf contract (per PERF_AUDIT_2026-08-09): idle init, ONE IntersectionObserver,
 * ONE capture-phase click listener, no MutationObserver, per-pageview dedupe.
 * Debug: add ?nalli_dl_debug=1 to the URL to console.log every emit.
 */

const GA4_ID = 'G-L2V9BMCRBL';
const DEBUG = /[?&]nalli_dl_debug=1/.test(location.search);
const THEME_ROLE = (window.Shopify && Shopify.theme && Shopify.theme.role) || 'unknown';

const seenViews = new Set();

/* ---------- transport ----------
 * dataLayer (GTM shape) fires immediately and unconditionally: it is a plain
 * array, so a push is never lost no matter what has or has not loaded.
 * gtag (GA4 shape) is GATED. gtag.js drops an `event` command whose `send_to`
 * target has not been configured yet, and the Google & YouTube app's tag loads
 * async, so an ungated push races it and silently loses events on fast
 * pageviews. Events emitted before the tag is ready are held and flushed in
 * order once `config G-L2V9BMCRBL` has been processed.
 */
let gtagReady = false;
const gtagQueue = [];

function ga4Configured() {
  return !!(window.google_tag_manager && window.google_tag_manager[GA4_ID]);
}

function flushGtag(reason) {
  gtagReady = true;
  window.dataLayer = window.dataLayer || [];
  while (gtagQueue.length) window.dataLayer.push(gtagQueue.shift());
  if (DEBUG) console.log('[nalli-dl] gtag flushed (' + reason + ')');
}

function waitForGtag() {
  if (ga4Configured()) { flushGtag('tag already configured'); return; }
  let waited = 0;
  const iv = setInterval(function () {
    waited += 250;
    if (ga4Configured()) {
      clearInterval(iv);
      flushGtag('tag ready after ' + waited + 'ms');
    } else if (waited >= 15000) {
      clearInterval(iv);
      // Best effort: push anyway. If the Google tag lands later it processes the
      // queued commands; if it never lands, nothing is lost that was not already.
      flushGtag('timeout at 15s, pushing unconfirmed');
    }
  }, 250);
}

function pushGtag(args) {
  window.dataLayer = window.dataLayer || [];
  if (gtagReady) window.dataLayer.push(args);
  else gtagQueue.push(args);
}

function emit(eventName, params) {
  const clean = {};
  for (const k in params) {
    if (params[k] !== undefined && params[k] !== null && params[k] !== '') clean[k] = params[k];
  }
  clean.theme_role = THEME_ROLE; // 'main' = live; anything else = test traffic, filterable in GA4
  if (DEBUG) console.log('[nalli-dl]', eventName, clean, gtagReady ? '' : '(gtag queued)');

  window.dataLayer = window.dataLayer || [];
  // GTM-shape push. NAMESPACED with a `nalli.` prefix on purpose: gtag('event', x)
  // already surfaces to a GTM container as event `x` with params under
  // `eventModel`, so an un-namespaced mirror would make the container see every
  // event TWICE. GTM triggers fire on `nalli.<short>`, never on `eventModel.*`.
  // The redundant `nalli_` on GA4-side names is stripped here so trigger names
  // read `nalli.section_view`, not `nalli.nalli_section_view`.
  window.dataLayer.push(
    Object.assign({ event: 'nalli.' + eventName.replace(/^nalli_/, '') }, clean)
  );
  // gtag-shape push (arguments), consumed by the Google tag on the main window.
  pushGtag(arguments2('event', eventName, Object.assign({ send_to: GA4_ID }, clean)));
}

// gtag.js only accepts real `arguments` objects; fabricate one.
function arguments2() {
  return arguments;
}

/* ---------- identity derivation ---------- */

function sectionOf(el) {
  const root = el.closest('.shopify-section');
  if (!root) return null;
  const id = (root.id || '').replace('shopify-section-', '');
  // template--123__new_slideshow_bA8R9B -> new_slideshow_bA8R9B ; sections--...__header -> header
  const key = id.split('__').pop() || id;
  return { root, id, key };
}

function textOf(el, max) {
  return (el.getAttribute('aria-label') || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, max || 80);
}

function nameOfLink(a) {
  const aria = a.getAttribute('aria-label');
  if (aria) return aria.slice(0, 80);
  const img = a.querySelector('img');
  if (img && img.alt) return img.alt.slice(0, 80);
  const heading = a.querySelector('h1,h2,h3,h4,h5') || a.closest('slideshow-slide, .resource-list__item, li, .card')?.querySelector('h1,h2,h3,h4,h5');
  if (heading && heading.textContent.trim()) return textOf(heading, 80);
  if (img && img.src) {
    const file = img.src.split('/').pop().split('?')[0];
    if (file) return decodeURIComponent(file).slice(0, 80);
  }
  const txt = textOf(a, 60);
  if (txt) return txt;
  try { return new URL(a.href).pathname.slice(0, 80); } catch (e) { return 'unknown'; }
}

/** Slide unit across both theme lineages: Horizon (v2.0.0) uses <slideshow-slide>,
 *  the live v1.1.x lineage uses Swiper's .swiper-slide. Keep both so this engine
 *  can ship to either theme unchanged. */
const SLIDE_SELECTOR = 'slideshow-slide, .swiper-slide';

function slideIndexOf(el) {
  const slide = el.closest(SLIDE_SELECTOR);
  if (!slide || !slide.parentElement) return null;
  // count only sibling slides (Swiper injects duplicates for loop mode, but
  // they sit in the same track so positional index still identifies the creative)
  const siblings = Array.prototype.filter.call(slide.parentElement.children, function (c) {
    return c.matches && c.matches(SLIDE_SELECTOR);
  });
  const i = siblings.indexOf(slide);
  return i >= 0 ? i + 1 : null;
}

function isPromoLink(a) {
  if (!a.querySelector('img, picture, svg')) return false;
  // product cards are handled by isPromoCard below, not as banner inventory
  if (a.closest('product-card, .product-card, product-card-link, [data-product-id], .predictive-search, .cart-drawer__items')) return false;
  // chrome (logo, nav images, drawers) is not promotional inventory;
  // menu interactions are covered by nalli_menu_click instead
  if (a.closest('header, footer, .menu-drawer, .cart-drawer, .header-section, [id*="__header"], [id*="__footer"], [id*="announcement"]')) return false;
  return !!a.closest('.shopify-section');
}

/* Curated homepage carousels ("Featured products", "Best Sellers", "Season's
 * Finest") are merchandising slots as much as product lists, so their cards
 * emit promo events carrying BOTH slot and product identity. Gated to an
 * explicit section-key pattern so collection, search and PDP grids never flood
 * the GA4 promotion reports; those stay with Shopify's native view_item_list.
 * Recently Viewed is excluded on purpose: it is personalised, not merchandised. */
const PROMO_CARD_SECTION = /featured_product|shop_by_collectives|best_seller/i;

/** Resolve to the REAL card root. Horizon repeats `data-product-id` on nested
 *  parts (card-gallery, quick-add-component, product-form-component,
 *  product-price), so matching that attribute first would treat one product as
 *  up to 5 separate promo slots. Card elements win; the attribute is only a
 *  fallback for lineages that have no <product-card> wrapper. */
function cardOf(el) {
  if (!el.closest) return null;
  return el.closest('product-card, .product-card') || el.closest('[data-product-id]');
}

function isPromoCard(el) {
  const card = cardOf(el);
  if (!card) return false;
  if (card.closest('header, footer, .menu-drawer, .cart-drawer, .predictive-search')) return false;
  const sec = sectionOf(card);
  return !!(sec && PROMO_CARD_SECTION.test(sec.key));
}

/** Anything that should emit view_promotion / select_promotion. */
function isPromoTarget(el) {
  if (el.tagName === 'A') return isPromoLink(el) || isPromoCard(el);
  return isPromoCard(el);
}

/** Banner tiles promote a DESTINATION rather than a product, but GA4 still
 *  requires an item, so synthesise a stable identity from the link target:
 *  /collections/varamahalakshmi -> collection:varamahalakshmi. */
function promoTargetId(href) {
  if (!href) return {};
  let path = href;
  try {
    path = new URL(href, location.origin).pathname;
  } catch (e) {
    /* relative or malformed href: fall back to the raw value */
  }
  const m = path.match(/\/(collections|products|pages|blogs)\/([^/?#]+)/);
  if (m) {
    const type = m[1].replace(/s$/, '');
    return { type: type, id: type + ':' + m[2] };
  }
  const clean = path.replace(/^\/+|\/+$/g, '');
  return { type: 'page', id: 'page:' + (clean || 'home') };
}

function promoParams(el) {
  const card = isPromoCard(el) ? cardOf(el) : null;
  const anchor = el.tagName === 'A' ? el : (card && card.querySelector('a[href]')) || null;
  const ref = card || anchor || el;
  const sec = sectionOf(ref);
  const idx = slideIndexOf(ref) || (function () {
    if (!sec) return null;
    if (card) {
      const cards = Array.prototype.slice.call(
        sec.root.querySelectorAll('product-card, .product-card')
      );
      const i = cards.indexOf(card);
      return i >= 0 ? i + 1 : null;
    }
    const links = Array.prototype.filter.call(sec.root.querySelectorAll('a[href]'), isPromoLink);
    const i = links.indexOf(anchor);
    return i >= 0 ? i + 1 : null;
  })();
  // the card's overlay <a> carries aria-label="{{ product.title }}", so
  // nameOfLink already resolves to the product title for cards
  const name = anchor ? nameOfLink(anchor) : textOf(ref, 80);
  const params = {
    promotion_id: sec ? sec.key + (idx ? ':' + idx : '') : undefined,
    promotion_name: name,
    creative_name: name,
    creative_slot: sec ? sec.key + (idx ? ':' + idx : '') : undefined,
    section_type: sec ? sec.key : undefined,
    section_id: sec ? sec.id : undefined,
    item_index: idx || undefined,
    link_url: anchor ? anchor.getAttribute('href') || undefined : undefined,
  };
  // GA4 REQUIRES an `items` array on view_promotion / select_promotion. Without
  // it the debugger reports "Missing list of items" and the Promotions report
  // has nothing to attribute the impression or click to. Identity goes here
  // rather than in flat item_* params, which would need custom dimensions and
  // collide with GA4's item-scoped reserved names.
  const item = { index: idx || undefined };
  if (card) {
    item.item_id = card.getAttribute('data-product-id') || undefined;
    item.item_name = name || undefined;
  } else {
    const target = promoTargetId(params.link_url);
    item.item_id = target.id || undefined;
    item.item_name = name || undefined;
    item.item_category = target.type || undefined;
  }
  for (const k in item) {
    if (item[k] === undefined) delete item[k];
  }
  if (item.item_id || item.item_name) params.items = [item];
  return params;
}

/* ---------- view tracking (one IO) ---------- */

const observed = new WeakSet();
let promoIO = null;

/** Re-runnable scan. isPromoLink is re-evaluated on every pass, so a link whose
 *  <img> lazy-loads after init is picked up by a later pass instead of being
 *  lost forever. Elements already observed are skipped via `observed`, and
 *  non-matching links are deliberately NOT marked, so they stay eligible. */
function scanTargets() {
  if (!promoIO) return;
  document.querySelectorAll('.shopify-section').forEach(function (sec) {
    if (observed.has(sec)) return;
    observed.add(sec);
    promoIO.observe(sec);
  });
  document.querySelectorAll('.shopify-section a[href]').forEach(function (a) {
    if (observed.has(a) || !isPromoLink(a)) return;
    observed.add(a);
    promoIO.observe(a);
  });
  // Cards are observed as the CARD element, not its <a>: the card link is an
  // empty overlay anchor that can measure zero-size, which would never trip
  // the observer.
  document
    .querySelectorAll('.shopify-section product-card, .shopify-section .product-card')
    .forEach(function (c) {
      if (observed.has(c) || !isPromoCard(c)) return;
      observed.add(c);
      promoIO.observe(c);
    });
}

function initViews() {
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(function (entries) {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const el = entry.target;
      io.unobserve(el);
      if (el.classList.contains('shopify-section')) {
        const id = (el.id || '').replace('shopify-section-', '');
        const key = id.split('__').pop() || id;
        if (seenViews.has('s:' + id)) continue;
        seenViews.add('s:' + id);
        emit('nalli_section_view', { section_type: key, section_id: id });
      } else {
        const p = promoParams(el);
        const dedupeKey = 'p:' + (p.promotion_id || '') + ':' + (p.promotion_name || '');
        if (seenViews.has(dedupeKey)) continue;
        seenViews.add(dedupeKey);
        emit('view_promotion', p);
      }
    }
  }, { threshold: 0.5 });

  promoIO = io;
  scanTargets();
  // Bounded re-scans (no MutationObserver, per the perf contract): catches
  // lazy-loaded carousel imagery and sections rendered after idle init.
  window.addEventListener('load', scanTargets, { once: true });
  setTimeout(scanTargets, 2500);
  setTimeout(scanTargets, 6000);
}

/* ---------- click tracking (one capture listener) ---------- */

/* One physical click on a JS-driven <details> can reach onClick several times;
 * see the guard in the expander branch. 300ms is well clear of the observed
 * 1-38ms repeats while still letting a deliberate open-then-close through. */
const EXPANDER_DEDUPE_MS = 300;
let lastExpander = null;
let lastExpanderAt = 0;

function onClick(e) {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;

  // wishlist heart (app-injected; capture-phase beats its stopPropagation)
  const wl = t.closest('.wishlist-engine, .wishlist-engine-button');
  if (wl) {
    const wrap = t.closest('[data-product_id], [data-product-id]') || wl;
    emit('nalli_wishlist_toggle', {
      product_id: wrap.getAttribute('data-product_id') || wrap.getAttribute('data-product-id') || undefined,
      was_added: wl.closest('[data-added]') ? wl.closest('[data-added]').getAttribute('data-added') : undefined,
      section_type: (sectionOf(t) || {}).key,
    });
    return;
  }

  // quick view
  if (t.closest('[class*="quick-view" i], [data-quick-view], quick-add-dialog [type="submit"]')) {
    const sec = sectionOf(t);
    emit('nalli_quick_view', { section_type: sec && sec.key });
    return;
  }

  // carousel arrows / pagination (Horizon + Swiper lineages)
  if (t.closest('slideshow-arrows, .nalli-slider-pagination, [ref="previousButton"], [ref="nextButton"], .slideshow-control, .swiper-button-next, .swiper-button-prev, .swiper-pagination, [class*="swiper-btn-" i]')) {
    const sec = sectionOf(t);
    emit('nalli_carousel_nav', { section_type: sec && sec.key, section_id: sec && sec.id });
    // Carousel slides render their cards lazily, so advancing the rail exposes
    // products that did not exist at any earlier scan. Cheaper and more
    // targeted than the MutationObserver the perf contract rules out.
    setTimeout(scanTargets, 400);
    return;
  }

  // header search / cart drawer openers
  if (t.closest('header [aria-label*="search" i], header button[class*="search" i]')) {
    emit('nalli_search_open', {});
    return;
  }
  if (t.closest('header [aria-label*="cart" i], header a[href="/cart"]')) {
    emit('nalli_drawer_open', { drawer: 'cart' });
    return;
  }

  // Menu expanders. These are <summary> elements, not links, so they fell
  // through the a[href] check below and menu OPENS were invisible: we saw the
  // link someone finally clicked, never the categories they browsed to get
  // there. Sits after the search/cart branches so those keep priority.
  const summary = t.closest('summary');
  if (summary && summary.closest('header, .menu-drawer, footer, [id*="__header"], [id*="__footer"]')) {
    // Some wrappers drive their <details> in JS ON TOP of the browser's native
    // summary toggle, so a single physical click arrives here several times
    // with `open` flipping between them. Measured on <nalli-currency-form>:
    // one click produced expand/collapse/expand 1-4ms apart, tripling the
    // event. Collapse repeats on the SAME summary inside a short window.
    const now = Date.now();
    if (summary === lastExpander && now - lastExpanderAt < EXPANDER_DEDUPE_MS) return;
    lastExpander = summary;
    lastExpanderAt = now;

    const details = summary.parentElement;
    // click fires BEFORE <details> toggles, so `open` still holds the old state
    const willOpen = !(details && details.open);
    let depth = 0;
    for (let n = summary.parentElement; n; n = n.parentElement) {
      if (n.tagName === 'DETAILS') depth++;
    }
    emit('nalli_menu_click', {
      menu_area: summary.closest('footer') ? 'footer' : 'header',
      link_text: textOf(summary, 60),
      menu_action: willOpen ? 'expand' : 'collapse',
      menu_depth: depth,
    });
    return;
  }

  const a = t.closest('a[href]');
  if (!a) return;

  // menu navigation (header/footer)
  if (a.closest('header, .menu-drawer, footer')) {
    emit('nalli_menu_click', {
      menu_area: a.closest('header, .menu-drawer') ? 'header' : 'footer',
      link_text: textOf(a, 60),
      link_url: a.getAttribute('href'),
    });
    return;
  }

  // banner, tile or merchandised product card click
  if (isPromoTarget(a)) {
    emit('select_promotion', promoParams(a));
  }
}

/* ---------- init (idle, storefront only) ---------- */

function init() {
  waitForGtag();
  initViews();
  document.addEventListener('click', onClick, { capture: true, passive: true });
  // tiny status handle for QA: nalliDL.ready() / nalliDL.queued()
  window.nalliDL = {
    ready: function () { return gtagReady; },
    queued: function () { return gtagQueue.length; },
    ga4: GA4_ID,
  };
  if (DEBUG) console.log('[nalli-dl] ready; sections:', document.querySelectorAll('.shopify-section').length);
}

if ('requestIdleCallback' in window) {
  requestIdleCallback(init, { timeout: 3000 });
} else {
  setTimeout(init, 1500);
}
